import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import { CONTACT_INFO } from '../data/mockData';

// TikTok icon component (not in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/share/17zCQTWG7F/?mibextid=wwXIfr', color: 'hover:text-[#1877F2]' },
    { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/rocketvanexpress', color: 'hover:text-[#E4405F]' },
    { name: 'TikTok', icon: TikTokIcon, url: 'https://tiktok.com/@rocketvanexpress', color: 'hover:text-white' },
    { name: 'YouTube', icon: Youtube, url: 'https://youtube.com/@rocketvanexpress', color: 'hover:text-[#FF0000]' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/rocketvanexpress', color: 'hover:text-[#0A66C2]' },
  ];

  return (
    <footer className="bg-[#001A47] text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_47b545fa-5451-4dff-82ef-01203d831594/artifacts/dmupxk74_Rocket_Van_Express_Logo_Main_Logo_RGB.png" 
                alt="Rocket Van Express" 
                className="h-16 w-auto mb-4"
              />
            </Link>
            <p className="text-gray-300 text-sm mb-4">
              Fast & reliable mini moves and deliveries across Stellenbosch and Cape Town
            </p>
            <p className="text-[#fb6402] font-semibold text-lg mb-4">Fast • Reliable</p>
            
            {/* Social Media Links */}
            <div className="flex items-center space-x-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-gray-400 transition-colors ${social.color}`}
                    aria-label={`Follow us on ${social.name}`}
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-lg mb-4">Services</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/deliveries" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Local Deliveries
                </Link>
              </li>
              <li>
                <Link to="/mini-moves" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Mini-Moves & Removals
                </Link>
              </li>
              <li>
                <Link to="/business" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Business Accounts
                </Link>
              </li>
              <li>
                <Link to="/areas" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Areas We Serve
                </Link>
              </li>
              <li>
                <Link to="/referral" className="text-[#fb6402] hover:text-[#ff8534] transition-colors text-sm font-medium">
                  Refer & Earn Free Deliveries
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="font-bold text-lg mb-4">Support</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/track" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Track Order
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Support Center
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Pricing Guide
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="lg:hidden">
            <h3 className="font-bold text-lg mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/terms" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Privacy Policy (POPIA)
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                  Cookie Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-4">Get in Touch</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-[#039fff] flex-shrink-0 mt-0.5" />
                <div>
                  <a href={`tel:${CONTACT_INFO.phone}`} className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-[#039fff] flex-shrink-0 mt-0.5" />
                <div>
                  <a href={`mailto:${CONTACT_INFO.email}`} className="text-gray-300 hover:text-[#fb6402] transition-colors text-sm">
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-[#039fff] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-300 text-sm">
                    Stellenbosch & Greater Cape Town
                  </p>
                </div>
              </li>
            </ul>
            <div className="mt-4">
              <p className="text-xs text-gray-400">{CONTACT_INFO.operatingHours}</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {currentYear} Rocket Van Express. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link to="/terms" className="text-sm text-gray-400 hover:text-[#fb6402] transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-[#fb6402] transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookies" className="text-sm text-gray-400 hover:text-[#fb6402] transition-colors">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
